import { flow, getSnapshot, types } from 'mobx-state-tree'
import { CustomerAccount, Invoice, routes } from '@linqpal/models'
import { crudSideBarStore } from '../crudSideBarStoreGenerator'
import { MessageSupport } from '../../../types'
import moment from 'moment'
import { InputReactionStore } from './InputReactionStore'
import { TabNotificationStore } from './TabNotificationStore'
import { toDeleteRegexPhoneNumberMask } from '@linqpal/models/src/helpers/validations'
import {
  invoiceStatus,
  statusBadge,
} from '@linqpal/models/src/dictionaries/invoiceStatus'
import { cancelableCrudSideBar } from '../../../../utils/types/enums'
import { isWeb } from '../../../../utils/helpers/commonUtils'
import { Linking } from 'react-native'
import { createContext, useContext } from 'react'
import { Invoice as InvoiceModel } from '../../../../../../models/src/mst/Invoice'
import makeInspectable from 'mobx-devtools-mst'
import { MESSAGE_TYPE } from '../../../../utils/helpers/MessageProvider'
import numbro from 'numbro'
import xlsx from 'xlsx'
import capitalize from 'lodash/capitalize'

const AMOUNT_REGEX = /[^0-9.-]/g

export const SupplierInvoicesTabStore = types
  .compose(
    types
      .model('SupplierInvoicesTabStore', {})
      .volatile((self) => ({
        businessAccounts: [],
        businessAccountsUsers: [],
        isLoadingBusinessAccounts: false,
        isLoadingTransactions: false,
        isDownloadingAttachment: false,
        customer: CustomerAccount.create({}),
        showCancel: false,
        transactions: [],
        discardChangesConfirmationModal: false,
        invoicesToCreate: [],
        isLoading: false,
        isDownloaded: false,
        hasInvoiceSent: false,
        type: 'invoice',
        showRequiredAlert: false,
        isFieldsPrefiled: false,
        fetchParams: {},
      }))
      .actions((self) => ({
        createCustomer(customer) {
          self.customer = CustomerAccount.create(customer)
        },
        checkRequiredFields() {
          return !(
            self.invoicesToCreate.size &&
            Array.from(self.invoicesToCreate.values()).every(
              (invoice) => invoice.invoice_number.length,
            )
          )
        },
        setIsDownloaded(status) {
          self.isDownloaded = status
        },
        setHasInvoiceSent(value) {
          self.hasInvoiceSent = value
        },
        setType(type) {
          self.type = type
        },
        setReceivableType(type) {
          self.setType(type)
          self.resetRefreshTable()
        },
        addInvoicesToCreate(id, invoice, activeIndex, fileName = '') {
          let index = id
          if (fileName) {
            // If any invoice in the uploaded list is saved / sent, it will be removed from array and index will change
            const newIndex = self.invoicesToCreate.findIndex((inv) =>
              inv?.document_name?.includes(fileName),
            )
            if (newIndex !== -1) index = newIndex
          }
          self.invoicesToCreate[index] = InvoiceModel.create(invoice)

          // set the data if invoice payload has the selected invoice
          if (typeof activeIndex === 'undefined' || index === activeIndex) {
            self.setDraftModel(self.invoicesToCreate[index])
            self.setBusinessNameFromAccountId()
            self.setCustomerFromAccountId()
          }
        },
        setEmptyInvoicesToCreate() {
          self.invoicesToCreate = []
        },
        setLoading(bool) {
          self.isLoading = bool
        },
        async sendInvoicesToCreate(activeIndex) {
          self.setLoading(true)

          const invoiceRes = await routes.invoices.saveInvoice({
            ...self.invoicesToCreate[activeIndex],
            status: invoiceStatus.placed,
            currentDate: moment().format('YYYY-MM-DD'),
            total_amount: self.invoicesToCreate[
              activeIndex
            ]?.total_amount?.replace(AMOUNT_REGEX, ''),
            material_subtotal: numbro.unformat(
              self.invoicesToCreate[activeIndex]?.material_subtotal,
            ),
            tax_amount: numbro.unformat(
              self.invoicesToCreate[activeIndex]?.tax_amount,
            ),
          })

          if (invoiceRes?.result === 'ok') {
            self.setHasInvoiceSent(true)
            self.setSuccessMessage(
              self.type === 'invoice'
                ? 'Invoice placed successfully'
                : 'SO placed successfully',
            )
            self.setIsDownloaded('ok')
            self.resetRefreshTable()
          } else {
            self.setHasInvoiceSent(false)
            self.setIsDownloaded('error')
          }

          self.setLoading(false)
        },
        async sendInvoicesToDraft(activeIndex) {
          self.setLoading(true)

          const invoiceRes = await routes.invoices.saveInvoice({
            ...self.invoicesToCreate[activeIndex],
            currentDate: moment().format('YYYY-MM-DD'),
            total_amount: self.invoicesToCreate[
              activeIndex
            ]?.total_amount?.replace(AMOUNT_REGEX, ''),
          })

          if (invoiceRes?.result === 'ok') {
            self.setHasInvoiceSent(true)
            self.setSuccessMessage(
              self.type === 'invoice'
                ? 'Invoice placed as draft successfully'
                : 'SO placed as draft successfully',
            )
            self.setIsDownloaded('ok')
            self.resetRefreshTable()
          } else {
            self.setHasInvoiceSent(false)
            self.setIsDownloaded('error')
          }

          self.setLoading(false)
        },

        removeFromInvoicesToCreate(index) {
          const filtered = self.invoicesToCreate.filter((inv, i) => {
            return i !== index
          })

          self.invoicesToCreate = filtered
        },
        showDiscardChangesConfirmationModal() {
          self.discardChangesConfirmationModal = true
        },
        hideDiscardChangesConfirmationModal() {
          self.discardChangesConfirmationModal = false
        },
        createDefaultCustomer() {
          self.customer = CustomerAccount.create({})
        },
        setShowRequiredAlert(bool) {
          self.showRequiredAlert = bool
        },
        setFieldsPrefiled(bool) {
          self.isFieldsPrefiled = bool
        },
      }))
      .views((self) => ({
        get isCancel() {
          return self.sideBarScreen === cancelableCrudSideBar.cancel
        },
        get isReject() {
          return self.sideBarScreen === cancelableCrudSideBar.reject
        },
        get hasCustomer() {
          return self.customer
        },
        get typeOfInvoice() {
          return capitalize(self?.draftModel?.type).replace('_', ' ')
        },
        get isQuote() {
          return self?.draftModel?.type === 'quote'
        },
        get receivableType() {
          const type = self.type
          return type === 'sales_order'
            ? 'SO'
            : type === 'invoice'
            ? 'Invoice'
            : 'Quote'
        },
      }))
      .actions((self) => ({
        startLoadingBusinessAccounts: () =>
          (self.loadingBusinessAccounts = true),
        stopLoadingBusinessAccounts: () =>
          (self.loadingBusinessAccounts = false),
        startLoadingTransactions: () => (self.isLoadingTransactions = true),
        stopLoadingTransactions: () => (self.isLoadingTransactions = false),
        startDownloadingAttachment: () => (self.isDownloadingAttachment = true),
        stopDownloadingAttachment: () => (self.isDownloadingAttachment = false),
        clearBusinessAccounts: () => (self.businessAccounts = null),
        loadBusinessAccounts: flow(function* () {
          self.startLoadingBusinessAccounts()
          try {
            const { items } = yield routes.supplier.allAccounts({
              search: '',
              sortColumn: '',
              sortDirection: '',
              page: 1,
              pageSize: 1000,
            })
            self.businessAccounts = items.map((account) =>
              CustomerAccount.create(account),
            )
          } catch (e) {
            self.setErrorMessage(e.message)
          } finally {
            self.stopLoadingBusinessAccounts()
          }
        }),
        checkAndLoadBusinessAccounts() {
          if (!self.isLoadingBusinessAccounts) {
            self.loadBusinessAccounts()
          }
        },
        loadAccountCustomers: () => {
          if (self.draftModel.customer_account_id) {
            self.businessAccountsUsers = self.businessAccounts.filter(
              (customerAccount) =>
                customerAccount.id === self.draftModel.customer_account_id,
            )
          } else {
            self.businessAccountsUsers = []
            self.draftModel.setValue('customer_contact', '')
          }
        },
        setBusinessNameFromAccountId() {
          const businessName = self.draftModel.customer_account_id
            ? self.businessAccounts.find(
                (account) =>
                  account.id === self.draftModel?.customer_account_id,
              )?.name
            : ''
          self.draftModel.setValue('business_name', businessName)
        },
        setCustomerFromAccountId() {
          const customer =
            self.draftModel.customer_account_id &&
            self.businessAccounts.find(
              (account) => account.id === self.draftModel?.customer_account_id,
            )
          self.customer = CustomerAccount.create(customer || {})
        },
        loadTransactions: flow(function* () {
          try {
            self.startLoadingTransactions()
            const { items } = yield routes.transactions.allTransactions({
              invoiceId: self.draftModel.id,
              search: '',
              includeNoPulls: true,
            })
            self.transactions = items
          } catch (e) {
            self.setErrorMessage(e.message)
          } finally {
            self.stopLoadingTransactions()
          }
        }),
        downloadAttachment: flow(function* () {
          try {
            self.startDownloadingAttachment()
            const { url } = yield routes.file.download(
              self.draftModel.invoice_document,
            )
            setTimeout(() => {
              if (isWeb) {
                window.open(url, '_blank')
              } else {
                Linking.openURL(url)
              }
            }, 100)
          } catch (e) {
            self.setErrorMessage(e.message)
          } finally {
            self.stopDownloadingAttachment()
          }
        }),
      }))
      .actions((self) => ({
        submit: flow(function* () {
          if (!self.isSending) {
            self.startSending()
            try {
              ;['invoice_date', 'invoice_due_date', 'expiration_date'].forEach(
                (date) => {
                  if (self[date]) self[date] = moment(self[date]).toISOString()
                },
              )
              yield routes.invoices.saveInvoice({
                ...getSnapshot(self.draftModel),
                currentDate: moment().format('YYYY-MM-DD'),
                material_subtotal: numbro.unformat(
                  self.draftModel.material_subtotal,
                ),
                tax_amount: numbro.unformat(self.draftModel.tax_amount),
                total_amount: self.draftModel.total_amount.replace(
                  AMOUNT_REGEX,
                  '',
                ),
                type: self?.type,
              })

              const receivableType = self.receivableType

              if (self.draftModel.isDeleted) {
                self.setErrorMessage(`This ${receivableType} has been deleted`)
              } else if (self.isCancel) {
                self.setErrorMessage(
                  `This ${receivableType} has been cancelled`,
                )
              } else {
                const successMessage =
                  self.draftModel.status === invoiceStatus.draft
                    ? `${receivableType} number ${self.draftModel.invoice_number} is saved as draft`
                    : `${receivableType} number ${self.draftModel.invoice_number} is placed`

                self.setSuccessMessage(successMessage)
              }
              self.createDefaultCustomer()
              self.createDefaultDraftModel()
              self.cancelAnySideBar()
              self.resetRefreshTable()
            } catch (e) {
              self.setErrorMessage(e.message)
            } finally {
              self.stopSending()
              self.isShowConfirmation = false
            }
          }
        }),
        approveInvoice: flow(function* () {
          if (!self.isSending) {
            self.startSending()
            try {
              ;['invoice_date', 'invoice_due_date', 'expiration_date'].forEach(
                (date) => {
                  if (self[date]) self[date] = moment(self[date]).toISOString()
                },
              )
              yield routes.invoices.approveInvoice({
                ...getSnapshot(self.draftModel),
                total_amount: self.draftModel.total_amount.replace(
                  AMOUNT_REGEX,
                  '',
                ),
              })

              const receivableType =
                self.type === 'sales_order' ? 'SO' : 'invoice'
              self.setSuccessMessage(`This ${receivableType} has been approved`)

              self.createDefaultCustomer()
              self.createDefaultDraftModel()
              self.cancelAnySideBar()
              self.refreshTable += 1
            } catch (e) {
              self.setErrorMessage(e.message)
            } finally {
              self.stopSending()
              self.isShowConfirmation = false
            }
          }
        }),
        rejectInvoice: flow(function* () {
          if (!self.isSending) {
            self.startSending()
            try {
              yield routes.invoices.rejectInvoice(self.draftModel.id)

              const receivableType =
                self.type === 'sales_order' ? 'SO' : 'invoice'
              self.setSuccessMessage(`This ${receivableType} has been rejected`)

              self.createDefaultCustomer()
              self.createDefaultDraftModel()
              self.cancelAnySideBar()
              self.refreshTable += 1
            } catch (e) {
              self.setErrorMessage(e.message)
            } finally {
              self.stopSending()
              self.isShowConfirmation = false
            }
          }
        }),
        makeInvoiceDraft() {
          self.draftModel.setValue('status', invoiceStatus.draft)
        },
        makeInvoicePlaced() {
          self.draftModel.setValue('status', invoiceStatus.placed)
          if (self.draftModel.company_id === '') {
            self.draftModel.setValue('approved', true)
          }
        },
        makeInvoiceApproved() {
          self.draftModel.setValue('approved', true)
          self.draftModel.setValue('status', invoiceStatus.placed)
        },
        submitWithSend() {
          self.makeInvoicePlaced()
          self.submit()
        },
        submitWithApprove() {
          self.makeInvoiceApproved()
          self.approveInvoice()
        },
        submitWithDraft() {
          self.makeInvoiceDraft()
          self.submit()
        },
        deleteInvoice() {
          self.draftModel.setValue('isDeleted', true)
          self.submit()
        },
        cancelInvoice: flow(function* () {
          self.startSending()
          try {
            yield routes.invoices.cancel(self.draftModel.id)

            const receivableType =
              self.type === 'sales_order' ? 'SO' : 'invoice'
            self.setErrorMessage(`This ${receivableType} has been cancelled`)
            self.createDefaultDraftModel()
            self.cancelAnySideBar()
            self.refreshTable += 1
          } catch (e) {
            self.setErrorMessage(e.message)
          } finally {
            self.stopSending()
            self.isShowConfirmation = false
          }
        }),
        cancelCancelSideBar: () =>
          self.unsetSideBar(cancelableCrudSideBar.cancel),
        showCancelSideBar: (model) => {
          self.cancelDeleteSideBar()
          self.setSideBar(cancelableCrudSideBar.cancel)
          if (model) {
            self.setDraftModel(model)
          }
        },
        showRejectSideBar: (model) => {
          self.cancelDeleteSideBar()
          self.setSideBar(cancelableCrudSideBar.reject)
          if (model) {
            self.setDraftModel(model)
          }
        },
      }))
      .actions((self) => ({
        invoiceConvertFromData(invoice) {
          self.customer = CustomerAccount.create(
            {
              ...invoice.customer,
              phone: invoice.customer?.phone?.replace(
                toDeleteRegexPhoneNumberMask,
                '',
              ),
            } || {},
          )
          return {
            ...invoice,
            phone: invoice.phone?.replace(toDeleteRegexPhoneNumberMask, ''),
            total_amount: invoice.total_amount?.toFixed(2),
            material_subtotal: invoice.material_subtotal?.toFixed(2),
            tax_amount: invoice.tax_amount?.toFixed(2),
            tax_exempted: !invoice.tax_amount,
          }
        },
        showViewSideBarWithConversion(invoice) {
          self.showViewSideBar(self.invoiceConvertFromData(invoice))
        },
        showEditSideBarWithConversion(invoice) {
          self.showEditSideBar(self.invoiceConvertFromData(invoice))
        },
        showNotifications() {
          self.tabNotifications.forEach((notification) => {
            if (
              Object.values(MESSAGE_TYPE).includes(
                notification.metadata.alertType,
              )
            )
              self.showNotification(
                notification.content,
                notification.metadata.alertType,
              )
          })
          self.markAllAsRead()
        },
        fetchInvoices(params) {
          self.fetchParams = params
          return routes.invoices.allInvoices(self.fetchParams)
        },
        async exportInvoicesTable(columns, params) {
          const query = params ?? self.fetchParams
          const invoicesResponse = await routes.invoices.allInvoices({
            ...query,
            pageSize: -1,
            page: -1,
          })

          if (invoicesResponse.length === 0) {
            return
          }

          const book = xlsx.utils.book_new()
          const filteredColumns = columns.filter(
            (x) => x.name && x.name !== ' ',
          )

          const formatStatusName = (invoice, fieldName) => {
            const status = statusBadge[invoice[fieldName]]?.label
            return invoice.status === invoiceStatus.pastDue && invoice.seen
              ? `${status} (Seen)`
              : status
          }
          xlsx.utils.book_append_sheet(
            book,
            xlsx.utils.json_to_sheet(
              invoicesResponse.items.map((item) => {
                return filteredColumns.reduce(
                  (p, cv) => ({
                    ...p,
                    [cv.name]:
                      cv.selector === 'status'
                        ? formatStatusName(item, cv.selector) ||
                          getDescendantProp(item, cv.selector)
                        : typeof cv.selector === 'string'
                        ? getDescendantProp(item, cv.selector)
                        : cv.selector(item),
                  }),
                  {},
                )
              }),
            ),
          )
          xlsx.writeFile(book, `${self.type}s.xlsx`, {
            bookType: 'xlsx',
          })
        },
      })),
    MessageSupport,
    crudSideBarStore(Invoice),
    InputReactionStore,
    TabNotificationStore('INVOICES'),
  )
  .named('SupplierTabInvoiceStore')

const supplierInvoiceTabStore = SupplierInvoicesTabStore.create()

makeInspectable(supplierInvoiceTabStore)

function getDescendantProp(obj, desc) {
  const arr = desc.split('.')
  while (arr.length > 0) {
    if (!obj) {
      return ''
    }

    obj = obj[arr.shift()]
  }

  return obj || ''
}

export const TabSupplierContext = createContext(supplierInvoiceTabStore)
export const TabSupplierProvider = TabSupplierContext.Provider

export const useTabInvoice = () => useContext(TabSupplierContext)
